/**
 * DATA LAYER (DL): MEMORAMA (MEMORY 1 VS 1)
 * Módulo de comunicación REST nativa (Fetch API) con el backend de Interaktia-V2 para la dinámica Memorama.
 * Provee métodos para el ciclo de vida completo de la partida: proyección de portada, escena de juego,
 * volteo autoritativo de cartas, ajuste de puntajes, histórico, configuración y subida optimizada de recursos gráficos.
 * 
 * @module utilities/DL/Memory
 */

/**
 * Resuelve y retorna la URL base para los endpoints del controlador Memory.
 * Compatible con rutas estándar de API en Interaktia-V2 ('/api/v1/memory' o '/api/memory').
 * 
 * @returns {string} URL base formateada para el controlador Memory.
 */
export const getApiBaseUrl = () => {
    const rawApiUrl = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const normalizedUrl = rawApiUrl.endsWith('/') ? rawApiUrl.slice(0, -1) : rawApiUrl;
    return `${normalizedUrl}/v1/Memory`;
};

/**
 * Sanea y valida identificadores o valores de cabecera HTTP para prevenir inyecciones CRLF.
 * 
 * @param {string|number|null} val - Valor a sanear.
 * @returns {string} Valor saneado sin saltos de línea ni caracteres de control.
 */
const sanitizeHeaderValue = (val) => {
    if (val === null || val === undefined) return '';
    return String(val).replace(/[\r\n\t\0]/g, '').trim().substring(0, 100);
};

/**
 * Construye las cabeceras HTTP estándar para las peticiones JSON.
 * Adjunta X-Room-Id si se proporciona para aislamiento multi-tenant.
 * 
 * @param {string|number|null} [roomId=null] - Identificador único de la sala o pantalla.
 * @param {Object} [customHeaders={}] - Cabeceras personalizadas adicionales.
 * @returns {Object} Objeto de cabeceras listo para Fetch API.
 */
const buildHeaders = (roomId = null, customHeaders = {}) => {
    const headers = {
        'Content-Type': 'application/json',
        ...customHeaders
    };

    if (roomId !== null && roomId !== undefined) {
        const cleanRoomId = sanitizeHeaderValue(roomId);
        if (cleanRoomId) {
            headers['X-Room-Id'] = cleanRoomId;
        }
    }

    return headers;
};

/**
 * Obtiene el identificador de la sala activa por defecto en el sistema.
 * 
 * @returns {Promise<{success: boolean, data?: {roomId: string}, message?: string}>} Sala activa resuelta.
 */
export const GetActiveRoomDL = async () => {
    try {
        const requestUrl = `${getApiBaseUrl()}/active-room`;
        const response = await fetch(requestUrl, {
            method: 'GET'
        });

        if (!response.ok) {
            return {
                success: false,
                data: null,
                message: 'No se pudo resolver la sala activa.'
            };
        }

        const responseData = await response.json();
        return {
            success: Boolean(responseData.success),
            data: responseData,
            message: responseData.message || 'Sala activa obtenida exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de conexión al consultar la sala activa de Memorama.'
        };
    }
};

// =========================================================================
// GESTIÓN DE TEMAS VISUALES Y BARAJAS
// =========================================================================

/**
 * Obtiene el catálogo de temas visuales disponibles para Memorama con filtrado multi-tenant.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de sala para temas personalizados.
 * @param {string|null} [operatorId=null] - Identificador GUID del operador.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Lista de temas disponibles.
 */
export const GetThemesDL = async (roomId = null, operatorId = null) => {
    try {
        const queryParams = new URLSearchParams();
        if (roomId) queryParams.append('roomId', String(roomId));
        if (operatorId) queryParams.append('operatorId', String(operatorId));

        const queryString = queryParams.toString();
        const requestUrl = `${getApiBaseUrl()}/themes${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: [],
                message: errorPayload.message || `Error HTTP ${response.status} al consultar los temas de Memorama.`
            };
        }

        const responseData = await response.json();
        const themes = responseData.data !== undefined ? responseData.data : (Array.isArray(responseData) ? responseData : []);
        return {
            success: true,
            data: themes,
            message: responseData.message || 'Temas obtenidos exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: [],
            message: 'Error de conexión o red al consultar los temas de Memorama.'
        };
    }
};

/**
 * Obtiene el detalle y configuración completa de un tema de Memorama por su ID.
 * 
 * @param {number|string} id - Identificador numérico del tema.
 * @param {string|null} [roomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Detalle del tema consultado.
 */
export const GetThemeByIdDL = async (id, roomId = null) => {
    try {
        const sanitizedId = encodeURIComponent(String(id));
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}`;

        const response = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al obtener el tema ${id}.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema obtenido exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: `Error de red al consultar el tema de Memorama con ID ${id}.`
        };
    }
};

/**
 * Crea un nuevo tema visual y baraja de Memorama.
 * 
 * @param {Object} themeData - Datos y personalización del tema.
 * @param {string} themeData.name - Nombre identificador del tema o patrocinador.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema creado y persistido.
 */
export const CreateThemeDL = async (themeData, roomId = null) => {
    try {
        const effectiveRoomId = (roomId && String(roomId).trim()) || (themeData?.roomId && String(themeData.roomId).trim()) || null;
        const requestUrl = `${getApiBaseUrl()}/theme`;

        const sanitizedPayload = {
            ...themeData,
            roomId: effectiveRoomId,
            operatorId: (themeData?.operatorId && String(themeData.operatorId).trim()) ? String(themeData.operatorId).trim() : null
        };

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(effectiveRoomId),
            body: JSON.stringify(sanitizedPayload)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al crear el tema de Memorama.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema creado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de conexión al crear el tema de Memorama.'
        };
    }
};

/**
 * Actualiza la configuración de un tema existente de Memorama.
 * 
 * @param {number|string} id - Identificador único del tema a modificar.
 * @param {Object} themeData - Datos actualizados del tema.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema actualizado.
 */
export const UpdateThemeDL = async (id, themeData, roomId = null) => {
    try {
        const sanitizedId = encodeURIComponent(String(id));
        const effectiveRoomId = (roomId && String(roomId).trim()) || (themeData?.roomId && String(themeData.roomId).trim()) || null;
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}`;

        const sanitizedPayload = {
            ...themeData,
            roomId: effectiveRoomId,
            operatorId: (themeData?.operatorId && String(themeData.operatorId).trim()) ? String(themeData.operatorId).trim() : null
        };

        const response = await fetch(requestUrl, {
            method: 'PUT',
            headers: buildHeaders(effectiveRoomId),
            body: JSON.stringify(sanitizedPayload)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al actualizar el tema ${id}.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema actualizado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: `Error de conexión al actualizar el tema con ID ${id}.`
        };
    }
};

/**
 * Elimina un tema personalizado de Memorama (los presets del sistema están protegidos).
 * 
 * @param {number|string} id - Identificador numérico del tema a eliminar.
 * @param {string|null} [roomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de eliminación.
 */
export const DeleteThemeDL = async (id, roomId = null) => {
    try {
        const sanitizedId = encodeURIComponent(String(id));
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}`;

        const response = await fetch(requestUrl, {
            method: 'DELETE',
            headers: buildHeaders(roomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al eliminar el tema.`
            };
        }

        const responseData = await response.json().catch(() => ({}));
        return {
            success: true,
            message: responseData.message || 'Tema eliminado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: `Error de red al eliminar el tema de Memorama con ID ${id}.`
        };
    }
};

/**
 * Clona un tema existente de Memorama como una nueva copia editable.
 * 
 * @param {number|string} id - Identificador único del tema origen a clonar.
 * @param {string|null} [newName=null] - Nombre para la nueva copia duplicada.
 * @param {string|null} [roomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema clonado.
 */
export const CloneThemeDL = async (id, newName = null, roomId = null) => {
    try {
        const sanitizedId = encodeURIComponent(String(id));
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}/clone`;

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({ newName: newName || undefined })
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al clonar el tema ${id}.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema clonado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: `Error de conexión al clonar el tema con ID ${id}.`
        };
    }
};

// =========================================================================
// CONFIGURACIÓN DE SALA / PANTALLA
// =========================================================================

/**
 * Obtiene la configuración activa de Memorama para una sala específica.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala o null para sala activa.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración activa de Memorama.
 */
export const GetConfigDL = async (roomId = null) => {
    try {
        const sanitizedRoomId = roomId ? encodeURIComponent(String(roomId)) : '';
        const requestUrl = sanitizedRoomId ? `${getApiBaseUrl()}/config/${sanitizedRoomId}` : `${getApiBaseUrl()}/config`;

        const response = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al obtener la configuración.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Configuración obtenida exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de red al consultar la configuración de Memorama.'
        };
    }
};

/**
 * Guarda o actualiza la configuración de Memorama para una sala (tema activo, duración, pares, desempate).
 * 
 * @param {Object} configData - Parámetros de configuración.
 * @param {string} configData.roomId - Identificador GUID de la sala.
 * @param {number|null} [configData.activeThemeId=null] - ID del tema asignado.
 * @param {number} [configData.roundDurationSeconds=60] - Tiempo de partida en segundos.
 * @param {number} [configData.pairsCount=6] - Número de pares a encontrar (3 a 12).
 * @param {string} [configData.tieBreakMode='least_flips'] - Criterio de desempate ('least_flips' | 'pure_tie' | 'fastest_match').
 * @param {number} [configData.previewTimeSeconds=3] - Segundos de memorización inicial (0 a 10).
 * @param {number} [configData.hideUnmatchedDelayMs=800] - Retardo en ms para ocultar pares erróneos (400 a 2000).
 * @param {string|null} [roomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración guardada.
 */
export const SaveConfigDL = async (configData, roomId = null) => {
    try {
        const effectiveRoomId = roomId || configData?.roomId || null;
        const requestUrl = `${getApiBaseUrl()}/config`;

        const payload = {
            roomId: effectiveRoomId,
            activeThemeId: configData?.activeThemeId !== undefined ? configData.activeThemeId : null,
            roundDurationSeconds: Number(configData?.roundDurationSeconds) || 60,
            pairsCount: Number(configData?.pairsCount) || 6,
            tieBreakMode: configData?.tieBreakMode || 'least_flips',
            previewTimeSeconds: configData?.previewTimeSeconds !== undefined ? Number(configData.previewTimeSeconds) : 3,
            hideUnmatchedDelayMs: configData?.hideUnmatchedDelayMs !== undefined ? Number(configData.hideUnmatchedDelayMs) : 800
        };

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(effectiveRoomId),
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al guardar la configuración.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Configuración guardada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de red al guardar la configuración de Memorama.'
        };
    }
};

// =========================================================================
// HISTORIAL DE PARTIDAS Y MÉTRICAS
// =========================================================================

/**
 * Obtiene el historial de partidas disputadas en Memorama para una sala específica.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {number} [limit=20] - Límite de partidas históricas a retornar.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Historial de partidas.
 */
export const GetMatchHistoryDL = async (roomId, limit = 20) => {
    try {
        if (!roomId) {
            return { success: false, data: [], message: 'El identificador de sala (roomId) es obligatorio.' };
        }

        const sanitizedRoomId = encodeURIComponent(String(roomId));
        const requestUrl = `${getApiBaseUrl()}/history/${sanitizedRoomId}?limit=${encodeURIComponent(String(limit || 20))}`;

        const response = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: [],
                message: errorPayload.message || `Error HTTP ${response.status} al obtener el historial.`
            };
        }

        const responseData = await response.json();
        const historyList = responseData.data !== undefined ? responseData.data : (Array.isArray(responseData) ? responseData : []);
        return {
            success: true,
            data: historyList,
            message: responseData.message || 'Historial obtenido exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: [],
            message: 'Error de red al consultar el historial de partidas de Memorama.'
        };
    }
};

/**
 * Registra y persiste las métricas y resultado final de una partida de Memorama.
 * 
 * @param {Object} matchData - Métricas de la partida completada.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado persistido.
 */
export const SaveMatchResultDL = async (matchData, roomId = null) => {
    try {
        const effectiveRoomId = roomId || matchData?.roomId || null;
        const requestUrl = `${getApiBaseUrl()}/history`;

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(effectiveRoomId),
            body: JSON.stringify(matchData || {})
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al guardar el resultado de la partida.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Resultado de la partida guardado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de conexión al persistir el resultado de la partida de Memorama.'
        };
    }
};

// =========================================================================
// SUBIDA OPTIMIZADA DE RECURSOS MULTIMEDIA
// =========================================================================

/**
 * Sube una imagen individual para fondos, avatares o cartas de Memorama (optimizado a WebP por el backend).
 * 
 * @param {File|Blob|FormData} fileOrFormData - Archivo binario o instancia de FormData a transmitir.
 * @param {string|null} [roomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, url?: string, data?: Object, message?: string}>} URL pública del recurso almacenado.
 */
export const UploadAssetDL = async (fileOrFormData, roomId = null) => {
    try {
        let requestFormData = fileOrFormData;
        if (!(fileOrFormData instanceof FormData)) {
            requestFormData = new FormData();
            requestFormData.append('file', fileOrFormData);
        }

        const headers = {};
        if (roomId !== null && roomId !== undefined) {
            const cleanRoomId = sanitizeHeaderValue(roomId);
            if (cleanRoomId) {
                headers['X-Room-Id'] = cleanRoomId;
            }
        }

        const requestUrl = `${getApiBaseUrl()}/upload`;

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: headers,
            body: requestFormData
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                url: '',
                message: errorPayload.message || `Error HTTP ${response.status} al subir la imagen.`
            };
        }

        const responseData = await response.json();
        const extractedUrl = responseData.url || responseData.data?.url || (typeof responseData === 'string' ? responseData : '');

        return {
            success: Boolean(responseData.success !== false),
            url: extractedUrl,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Imagen subida exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            url: '',
            message: 'Error de red al procesar la subida del recurso de Memorama.'
        };
    }
};

/**
 * Sube un lote de hasta 50 imágenes simultáneamente para barajas de Memorama.
 * 
 * @param {Array<File>|FileList|FormData} filesOrFormData - Colección de archivos a transmitir en lote.
 * @param {string|null} [roomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, urls?: Array<string>, data?: Object, message?: string}>} Colección de URLs generadas.
 */
export const UploadBatchCardsDL = async (filesOrFormData, roomId = null) => {
    try {
        let requestFormData = filesOrFormData;
        if (!(filesOrFormData instanceof FormData)) {
            requestFormData = new FormData();
            const fileList = Array.isArray(filesOrFormData) ? filesOrFormData : Array.from(filesOrFormData || []);
            fileList.forEach((file) => {
                requestFormData.append('files', file);
            });
        }

        const headers = {};
        if (roomId !== null && roomId !== undefined) {
            const cleanRoomId = sanitizeHeaderValue(roomId);
            if (cleanRoomId) {
                headers['X-Room-Id'] = cleanRoomId;
            }
        }

        const requestUrl = `${getApiBaseUrl()}/upload-batch`;

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: headers,
            body: requestFormData
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                urls: [],
                message: errorPayload.message || `Error HTTP ${response.status} al subir el lote de imágenes.`
            };
        }

        const responseData = await response.json();
        const extractedUrls = responseData.urls || responseData.data?.urls || (Array.isArray(responseData.files) ? responseData.files.map(f => f.url).filter(Boolean) : []);

        return {
            success: Boolean(responseData.success !== false),
            urls: extractedUrls,
            data: responseData,
            message: responseData.message || 'Lote de imágenes procesado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            urls: [],
            message: 'Error de red al subir el lote de imágenes para barajas de Memorama.'
        };
    }
};

// =========================================================================
// CONTROL DE PARTIDA EN TIEMPO REAL Y OPERACIÓN
// =========================================================================

/**
 * Obtiene el estado en vivo de la partida de Memorama en la sala dada con filtrado Zero-Knowledge.
/**
 * Obtiene el estado en vivo de la partida de Memorama en la sala dada con filtrado Zero-Knowledge.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala.
 * @param {string|null} [forSide=null] - Lado opcional del jugador ("player1" o "player2").
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado en vivo de la partida.
 */
export const GetMatchStateDL = async (roomId = null, forSide = null) => {
    try {
        const sanitizedRoomId = roomId ? encodeURIComponent(String(roomId)) : '';
        const queryParams = new URLSearchParams();
        if (forSide) queryParams.append('forSide', String(forSide));

        const queryString = queryParams.toString();
        const requestUrl = sanitizedRoomId 
            ? `${getApiBaseUrl()}/match/state/${sanitizedRoomId}${queryString ? `?${queryString}` : ''}`
            : `${getApiBaseUrl()}/match/state${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al consultar el estado de la partida.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Estado de partida obtenido exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de conexión al obtener el estado en vivo de Memorama.'
        };
    }
};

/**
 * Proyecta la portada de Memorama en la pantalla gigante y dispositivos móviles.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala.
 * @param {number|null} [themeId=null] - ID opcional del tema a proyectar.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado actualizado en portada.
 */
export const ShowCoverDL = async (roomId = null, themeId = null) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/match/cover`;
        const payload = {
            roomId: roomId ? String(roomId) : undefined,
            themeId: themeId !== null && themeId !== undefined ? Number(themeId) : null
        };

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al mostrar la portada.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Portada de Memorama proyectada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de red al proyectar la portada de Memorama.'
        };
    }
};

/**
 * Proyecta la escena de juego 1 vs 1 y genera los tableros barajados en memoria.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala.
 * @param {number|null} [themeId=null] - ID opcional del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado de juego preparado.
 */
export const ShowGameSceneDL = async (roomId = null, themeId = null) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/match/game`;
        const payload = {
            roomId: roomId ? String(roomId) : undefined,
            themeId: themeId !== null && themeId !== undefined ? Number(themeId) : null
        };

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al preparar la escena de juego.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Escena de juego de Memorama preparada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de red al proyectar la escena de juego de Memorama.'
        };
    }
};

/**
 * Inicia la secuencia oficial de partida (conteo regresivo 3..2..1 -> preview -> playing).
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala.
 * @param {Object} [matchOptions={}] - Opciones de configuración de partida visibles para el operador.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de inicio de secuencia.
 */
export const StartMatchDL = async (roomId = null, matchOptions = {}) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/match/start`;
        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({
                roomId: roomId ? String(roomId) : undefined,
                roundDurationSeconds: matchOptions.roundDurationSeconds !== undefined ? Number(matchOptions.roundDurationSeconds) : undefined,
                pairsCount: matchOptions.pairsCount !== undefined ? Number(matchOptions.pairsCount) : undefined,
                tieBreakMode: matchOptions.tieBreakMode || undefined,
                previewTimeSeconds: matchOptions.previewTimeSeconds !== undefined ? Number(matchOptions.previewTimeSeconds) : undefined,
                hideUnmatchedDelayMs: matchOptions.hideUnmatchedDelayMs !== undefined ? Number(matchOptions.hideUnmatchedDelayMs) : undefined,
                themeId: matchOptions.themeId !== undefined ? Number(matchOptions.themeId) : undefined
            })
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al iniciar la partida.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Partida de Memorama iniciada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de red al iniciar la secuencia de partida de Memorama.'
        };
    }
};

/**
 * Asigna a un participante a uno de los dos slots ("player1" o "player2").
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala.
 * @param {string} side - Lado seleccionado ("player1" o "player2").
 * @param {string} [playerName=''] - Nombre del participante móvil.
 * @param {string} [connectionId=''] - Identificador SignalR de conexión del cliente.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado de la asignación.
 */
export const SelectPlayerSideDL = async (roomId = null, side = 'player1', playerName = '', connectionId = '', accessPin = '') => {
    try {
        const requestUrl = `${getApiBaseUrl()}/match/select-side`;
        const payload = {
            roomId: roomId ? String(roomId) : undefined,
            side: String(side || 'player1'),
            playerName: String(playerName || '').trim(),
            connectionId: String(connectionId || ''),
            accessPin: accessPin ? String(accessPin).trim() : undefined
        };

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al seleccionar personaje.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Personaje asignado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de conexión al asignar personaje en Memorama.'
        };
    }
};

/** Alias de SelectPlayerSideDL para integración con el cliente aficionado */
export const RegisterFanPlayerDL = SelectPlayerSideDL;

/**
 * Procesa el volteo autoritativo de una carta de Memorama en el backend.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala.
 * @param {string} playerSide - Lado del jugador ("player1" o "player2").
 * @param {number} cardIndex - Índice numérico de la carta (0 a 23).
 * @param {string|null} [connectionId=null] - Identificador de conexión del cliente.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado del volteo y evaluación de acierto.
 */
export const FlipCardDL = async (roomId = null, playerSide = 'player1', cardIndex = 0, connectionId = null) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/match/flip`;
        const payload = {
            roomId: roomId ? String(roomId) : undefined,
            playerSide: String(playerSide || 'player1'),
            cardIndex: Number(cardIndex),
            connectionId: connectionId ? String(connectionId) : undefined
        };

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al voltear la carta.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Carta procesada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de red al registrar el volteo de carta en Memorama.'
        };
    }
};

/**
 * Actualiza los nombres de los jugadores en caliente desde la consola de operador.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala.
 * @param {string|null} [player1Name=null] - Nombre para Jugador 1.
 * @param {string|null} [player2Name=null] - Nombre para Jugador 2.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado con nombres actualizados.
 */
export const UpdatePlayerNamesDL = async (roomId = null, player1Name = null, player2Name = null) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/match/update-names`;
        const payload = {
            roomId: roomId ? String(roomId) : undefined,
            player1Name: player1Name !== null ? String(player1Name).trim() : undefined,
            player2Name: player2Name !== null ? String(player2Name).trim() : undefined
        };

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al actualizar nombres.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Nombres actualizados exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de conexión al actualizar nombres en Memorama.'
        };
    }
};

/**
 * Control de emergencia: Ajusta el puntaje (+1 / -1 pt) de un participante.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala.
 * @param {string} playerSide - Lado del jugador ("player1" o "player2").
 * @param {number} [delta=1] - Variación de puntaje (+1 o -1).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado con puntaje ajustado.
 */
export const AdjustScoreDL = async (roomId = null, playerSide = 'player1', delta = 1) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/match/adjust-score`;
        const payload = {
            roomId: roomId ? String(roomId) : undefined,
            playerSide: String(playerSide || 'player1'),
            delta: Number(delta) || 1
        };

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al ajustar puntaje.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Puntaje ajustado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de red al ajustar puntaje en Memorama.'
        };
    }
};

/**
 * Control de emergencia / revancha: Reinicia la partida al estado 'game_ready' barajando nuevos tableros.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado reiniciado.
 */
export const ResetMatchDL = async (roomId = null) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/match/reset`;
        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({ roomId: roomId ? String(roomId) : undefined })
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al reiniciar la partida.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Partida reiniciada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de conexión al reiniciar la partida de Memorama.'
        };
    }
};

/**
 * Control de emergencia: Finaliza anticipadamente la partida declarando un ganador o motivo forzado.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala.
 * @param {string|null} [winner=null] - Ganador ("player1", "player2" o "tie").
 * @param {string|null} [reason=null] - Motivo de finalización (ej. "admin_forced").
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado finalizado.
 */
export const FinishMatchEarlyDL = async (roomId = null, winner = null, reason = null) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/match/finish`;
        const payload = {
            roomId: roomId ? String(roomId) : undefined,
            winner: winner ? String(winner) : undefined,
            reason: reason ? String(reason) : 'admin_forced'
        };

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al finalizar la partida.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Partida finalizada anticipadamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de red al finalizar anticipadamente Memorama.'
        };
    }
};

/**
 * Procesa la salida voluntaria de un jugador de la partida.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala.
 * @param {string|null} [connectionId=null] - Identificador de conexión del cliente.
 * @param {string|null} [playerSide=null] - Lado liberado ("player1" o "player2").
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado tras la salida del jugador.
 */
export const LeaveGameDL = async (roomId = null, connectionId = null, playerSide = null) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/match/leave`;
        const payload = {
            roomId: roomId ? String(roomId) : undefined,
            connectionId: connectionId ? String(connectionId) : undefined,
            playerSide: playerSide ? String(playerSide) : undefined
        };

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                data: null,
                message: errorPayload.message || `Error HTTP ${response.status} al abandonar el juego.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Jugador abandonó la partida.'
        };
    } catch (networkError) {
        return {
            success: false,
            data: null,
            message: 'Error de red al procesar la salida voluntaria de Memorama.'
        };
    }
};

/**
 * Libera ambos lugares de jugadores (desconecta a J1 y J2 pacíficamente).
 * 
 * @param {string|number|null} [roomId=null] - Identificador de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado de la operación.
 */
export const ClearPlayersDL = async (roomId = null) => {
    try {
        const url = `${getApiBaseUrl()}/match/clear-players`;
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({ roomId: String(roomId || '') })
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, data: null, message: err.message || `Error HTTP ${response.status} al liberar jugadores.` };
        }
        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Lugares de jugadores liberados exitosamente.'
        };
    } catch (networkError) {
        return { success: false, data: null, message: 'Error de red al liberar jugadores de Memorama.' };
    }
};

/**
 * Cambia o regenera el PIN de acceso a la sala de Memorama.
 * 
 * @param {string|number|null} [roomId=null] - Identificador de sala.
 * @param {string|null} [newPin=null] - Nuevo PIN opcional.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado de la operación.
 */
export const ChangeAccessPinDL = async (roomId = null, newPin = null) => {
    try {
        const url = `${getApiBaseUrl()}/match/change-pin`;
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({ roomId: String(roomId || ''), newPin: newPin || null })
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, data: null, message: err.message || `Error HTTP ${response.status} al actualizar PIN.` };
        }
        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'PIN de acceso actualizado exitosamente.'
        };
    } catch (networkError) {
        return { success: false, data: null, message: 'Error de red al actualizar PIN de Memorama.' };
    }
};

/** Alias de LeaveGameDL */
export const UnregisterFanPlayerDL = LeaveGameDL;

export default {
    getApiBaseUrl,
    GetActiveRoomDL,
    GetThemesDL,
    GetThemeByIdDL,
    CreateThemeDL,
    UpdateThemeDL,
    DeleteThemeDL,
    CloneThemeDL,
    GetConfigDL,
    SaveConfigDL,
    GetMatchHistoryDL,
    SaveMatchResultDL,
    UploadAssetDL,
    UploadBatchCardsDL,
    GetMatchStateDL,
    ShowCoverDL,
    ShowGameSceneDL,
    StartMatchDL,
    SelectPlayerSideDL,
    RegisterFanPlayerDL,
    FlipCardDL,
    UpdatePlayerNamesDL,
    AdjustScoreDL,
    ResetMatchDL,
    FinishMatchEarlyDL,
    LeaveGameDL,
    UnregisterFanPlayerDL,
    ClearPlayersDL,
    ChangeAccessPinDL
};
